import { ArrowRight, ArrowUpRight, Check, Mail, MapPin, PackageCheck, Phone, Ruler, Wrench } from "lucide-react";
import React from "react";
import { Link, useRoute } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { getCityServiceAreasForState, getTopFiveCityMarketsForState } from "@/data/cityServiceAreas";
import { serviceStates } from "@/data/serviceAreas";
import { getStateAreaPage } from "@/data/stateAreaPages";
import { assets, realPhotos, siteConfig } from "@/data/site";
import NotFound from "./NotFound";

export default function StateServiceArea() {
  const [, params] = useRoute<{ slug: string }>("/service-areas/:slug");
  const page = getStateAreaPage(params?.slug);
  const state = serviceStates.find((item) => item.slug === params?.slug);

  if (!page || !state) return <NotFound />;

  const canonicalPath = `/service-areas/${state.slug}`;
  const url = `${siteConfig.url}${canonicalPath}`;
  const quoteHref = `/request-a-quote?serviceArea=${state.slug}`;
  const cityGuides = getCityServiceAreasForState(state.slug);
  const topFiveCityMarkets = getTopFiveCityMarketsForState(state.slug);
  const topFiveCityRoutes = new Set(topFiveCityMarkets.map((market) => market.route));
  const additionalCityGuides = cityGuides.filter((guide) => !topFiveCityRoutes.has(guide.route));
  const projectEntries = (page.featuredProjects ?? []).map((project) => ({
    project,
    projectPhoto: realPhotos.projects[project.imageKey],
    galleryPhotos: project.gallery?.images.map((image) => ({ ...image, sources: realPhotos.projects[image.imageKey] })) ?? [],
  }));
  const primaryProjectPhoto = projectEntries[0]?.projectPhoto;
  const structuredProjectImages = projectEntries.flatMap(({ project, projectPhoto, galleryPhotos }) => (
    project.imageKind === "material-reference"
      ? []
      : [projectPhoto.large, ...galleryPhotos.map((image) => image.sources.large)]
  ));
  const projectListSchema = projectEntries.length
    ? {
        "@type": "ItemList",
        "@id": `${url}#projects`,
        name: `Owner-confirmed Partition Stalls projects in ${state.name}`,
        numberOfItems: projectEntries.length,
        itemListElement: projectEntries.map(({ project, projectPhoto }, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "CreativeWork",
            "@id": `${url}#project-${index + 1}`,
            name: project.title,
            description: project.summary,
            ...(project.imageKind === "material-reference" ? {} : { image: `${siteConfig.url}${projectPhoto.large}` }),
            url: `${url}#state-project-title-${index}`,
            about: { "@id": `${url}#service` },
            contentLocation: { "@type": "Place", name: project.location },
            publisher: { "@id": `${siteConfig.url}/#business` },
          },
        })),
      }
    : undefined;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Service Areas", item: `${siteConfig.url}/service-areas` },
          { "@type": "ListItem", position: 3, name: state.name, item: url },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: page.metaTitle,
        description: page.metaDescription,
        isPartOf: { "@id": `${siteConfig.url}/#website` },
        about: { "@id": `${url}#service` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
        mentions: projectListSchema ? { "@id": `${url}#projects` } : undefined,
        primaryImageOfPage: `${siteConfig.url}${primaryProjectPhoto?.large ?? assets.serviceAreasArtworkLarge}`,
        image: structuredProjectImages.length
          ? structuredProjectImages.map((image) => `${siteConfig.url}${image}`)
          : [`${siteConfig.url}${assets.serviceAreasArtworkLarge}`],
      },
      {
        "@type": "Service",
        "@id": `${url}#service`,
        name: `Commercial bathroom partition supply and installation review in ${state.name}`,
        serviceType: "Commercial bathroom partition and restroom accessory supply with project-specific installation review",
        description: page.serviceStatement,
        provider: { "@id": `${siteConfig.url}/#business` },
        areaServed: { "@type": "State", name: state.name },
        url,
      },
      ...(projectListSchema ? [projectListSchema] : []),
    ],
  };

  return (
    <>
      <StructuredData data={structuredData} />

      <header className="state-area-hero section-line">
        <div className="state-area-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/service-areas">Service areas</Link><span>/</span>{state.name}</p>
          <span className="kicker">{state.name} · Supply + installation</span>
          <h1>{page.heroTitle}</h1>
          <p>{page.heroCopy}</p>
          <div className="button-row">
            <Link href={quoteHref} className="button button--primary">Request a {state.abbreviation} Quote <ArrowUpRight aria-hidden="true" /></Link>
            <a href={`tel:${siteConfig.phoneHref}`} className="button button--outline"><Phone aria-hidden="true" /> Call {siteConfig.phoneDisplay}</a>
          </div>
        </div>
        <div className="state-area-hero__map" aria-label={`${state.name} full-service coverage overview`}>
          <span className="state-area-hero__index">{state.abbreviation}</span>
          <img src={state.icon} alt={`Outline map of ${state.name}`} width="320" height="320" fetchPriority="high" />
          <div>
            <span>Five leading city guides</span>
            <p>{topFiveCityMarkets.map((market) => market.city).join(" · ")}</p>
          </div>
        </div>
      </header>

      <section className="state-area-scope section-line" aria-labelledby="state-service-title">
        <article>
          <Wrench aria-hidden="true" />
          <span className="kicker">Full-service state</span>
          <h2 id="state-service-title">Supply and project-specific installation review.</h2>
          <p>{page.serviceStatement}</p>
        </article>
        <article>
          <PackageCheck aria-hidden="true" />
          <span className="kicker">One coordinated scope</span>
          <h2>Partitions and accessories, planned together.</h2>
          <p>Compare all five partition material families, four mounting systems, privacy options, and commercial restroom accessories before the quote is finalized.</p>
          <div className="state-area-scope__links"><Link href="/bathroom-partitions">Compare materials <ArrowRight aria-hidden="true" /></Link><Link href="/commercial-restroom-accessories">Browse accessories <ArrowRight aria-hidden="true" /></Link></div>
        </article>
      </section>

      {projectEntries.map(({ project, projectPhoto, galleryPhotos }, projectIndex) => (
        <React.Fragment key={project.imageKey}>
          <section className="state-area-project section-line" aria-labelledby={`state-project-title-${projectIndex}`}>
            <figure className="state-area-project__media">
              <picture>
                <source media="(max-width: 640px)" srcSet={projectPhoto.small} />
                <source media="(max-width: 1100px)" srcSet={projectPhoto.medium} />
                <img
                  src={projectPhoto.large}
                  srcSet={`${projectPhoto.small} 640w, ${projectPhoto.medium} 1000w, ${projectPhoto.large} 1600w`}
                  sizes="(max-width: 900px) 100vw, 58vw"
                  alt={project.imageAlt}
                  width={project.imageWidth}
                  height={project.imageHeight}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <figcaption>{project.primaryCaption}</figcaption>
            </figure>
            <article className="state-area-project__details">
              <span className="kicker">{project.kicker}</span>
              <p className="state-area-project__location"><MapPin aria-hidden="true" /> {project.location}</p>
              <h2 id={`state-project-title-${projectIndex}`}>{project.title}</h2>
              <p className="state-area-project__summary">{project.summary}</p>
              <dl className="state-area-project__metrics">
                {project.metrics.map((metric) => (
                  <div key={metric.label}><dt>{metric.value}</dt><dd>{metric.label}</dd></div>
                ))}
              </dl>
              <dl className="state-area-project__facts">
                {project.facts.map((fact) => (
                  <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>
                ))}
              </dl>
              <p className="state-area-project__disclosure">{project.disclosure}</p>
              <div className="state-area-project__actions">
                <Link href={quoteHref} className="button button--primary">Start a {state.name} Project <ArrowUpRight aria-hidden="true" /></Link>
                {project.venueUrl && project.venueName ? <a href={project.venueUrl} target="_blank" rel="noreferrer" className="text-link">{project.venueName} <ArrowUpRight aria-hidden="true" /></a> : null}
              </div>
            </article>
          </section>

          {project.gallery && galleryPhotos.length ? (
            <section className="state-area-venue section-line" aria-labelledby={`state-project-gallery-title-${projectIndex}`}>
              <div className="state-area-venue__intro">
                <span className="kicker">{project.gallery.kicker}</span>
                <h2 id={`state-project-gallery-title-${projectIndex}`}>{project.gallery.title}</h2>
                <p>{project.gallery.copy}</p>
                {project.venueUrl ? <a href={project.venueUrl} target="_blank" rel="noreferrer" className="text-link">{project.gallery.linkLabel} <ArrowUpRight aria-hidden="true" /></a> : null}
              </div>
              <div className="state-area-venue__gallery">
                {galleryPhotos.map((image) => (
                  <figure key={image.imageKey}>
                    <picture>
                      <source media="(max-width: 640px)" srcSet={image.sources.small} />
                      <source media="(max-width: 1100px)" srcSet={image.sources.medium} />
                      <img
                        src={image.sources.large}
                        srcSet={`${image.sources.small} 640w, ${image.sources.medium} 1000w, ${image.sources.large} 1600w`}
                        sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 33vw"
                        alt={image.alt}
                        width={image.width}
                        height={image.height}
                        loading="lazy"
                        decoding="async"
                      />
                    </picture>
                    <figcaption>
                      <strong>{image.caption}</strong>
                      {image.credit ? (
                        <>
                          <span>Photo: <a href={image.credit.sourceUrl} target="_blank" rel="noreferrer">{image.credit.creator}</a> · <a href={image.credit.licenseUrl} target="_blank" rel="noreferrer">{image.credit.licenseLabel}</a></span>
                          <small>{image.credit.changes}</small>
                        </>
                      ) : (
                        <span>Owner-provided project photograph · Partition Stalls</span>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>
          ) : null}
        </React.Fragment>
      ))}

      <section className="state-area-markets section-line" aria-labelledby="state-markets-title">
        <div className="state-area-section-heading">
          <div><span className="kicker">Local planning context</span><h2 id="state-markets-title">Three {state.name} markets. Three different restroom conditions.</h2></div>
          <p>{page.marketIntro}</p>
        </div>
        <div className="state-area-market-grid">
          {page.markets.map((market, index) => (
            <article key={market.name}>
              <div><span>0{index + 1}</span><MapPin aria-hidden="true" /></div>
              <h3>{market.name}</h3>
              <p className="state-area-market-grid__mix">{market.facilityMix}</p>
              <p>{market.planning}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="state-area-city-directory section-line" aria-labelledby="state-city-guides-title">
        <div><span className="kicker">Five leading city guides</span><h2 id="state-city-guides-title">Five population-leading {state.name} locations. One project-specific review.</h2><p>These guides follow the five largest incorporated places in the state by U.S. Census Bureau Vintage 2025 July 1 population estimate. City names are project locations, not claims of a separate office, stocked warehouse, or automatic acceptance of every project condition.</p><a className="text-link" href="https://www.census.gov/data/tables/time-series/demo/popest/2020s-total-cities-and-towns.html" target="_blank" rel="noreferrer">Census city-and-town population estimates <ArrowUpRight aria-hidden="true" /></a>{additionalCityGuides.length ? <p className="state-area-city-directory__additional">Additional planning guides: {additionalCityGuides.map((guide, index) => <React.Fragment key={guide.route}>{index ? " · " : ""}<Link href={guide.route}>{guide.city}</Link></React.Fragment>)}</p> : null}</div>
        <nav aria-label={`${state.name} city and metro commercial restroom guides`}>
          {topFiveCityMarkets.map((market) => <Link key={market.route} href={market.route}><span>{state.abbreviation} · Census 2025 · #{market.rank}</span><strong>{market.city}</strong><p>{market.population.toLocaleString("en-US")} estimated residents · project-specific review</p><ArrowRight aria-hidden="true" /></Link>)}
        </nav>
      </section>

      <section className="state-area-priorities section-line" aria-labelledby="state-priorities-title">
        <div className="state-area-priorities__intro"><span className="kicker">Specify for the real facility</span><h2 id="state-priorities-title">What to discuss before product selection.</h2><p>{page.priorityIntro}</p><Link href="/resources/mounting-styles" className="text-link">Compare mounting systems <ArrowRight aria-hidden="true" /></Link></div>
        <div className="state-area-priority-grid">
          {page.priorities.map((priority, index) => <article key={priority.title}><span>0{index + 1}</span><h3>{priority.title}</h3><p>{priority.copy}</p></article>)}
        </div>
      </section>

      <section className="state-area-quote-kit section-line" aria-labelledby="state-quote-kit-title">
        <div className="state-area-quote-kit__intro"><Ruler aria-hidden="true" /><span className="kicker kicker--light">Quote preparation</span><h2 id="state-quote-kit-title">Send the details that change the price.</h2><p>{page.quoteIntro}</p><div><a href={`mailto:${siteConfig.email}`}><Mail aria-hidden="true" /> {siteConfig.email}</a><a href={`tel:${siteConfig.phoneHref}`}><Phone aria-hidden="true" /> {siteConfig.phoneDisplay}</a></div></div>
        <ol>{page.quoteChecklist.map((item, index) => <li key={item}><span>0{index + 1}</span><p>{item}</p><Check aria-hidden="true" /></li>)}</ol>
      </section>

      <section className="state-area-faq section-line" aria-labelledby="state-faq-title">
        <div className="state-area-section-heading"><div><span className="kicker">{state.name} buyer questions</span><h2 id="state-faq-title">Straight answers before you request pricing.</h2></div><p>Product and installation decisions remain project-specific. Plans, field conditions, manufacturer requirements, schedule, and the responsible design team’s review control the final selection.</p></div>
        <div className="state-area-faq__list">{page.faqs.map((faq, index) => <article key={faq.question}><span>0{index + 1}</span><div><h3>{faq.question}</h3><p>{faq.answer}</p></div></article>)}</div>
      </section>

      <aside className="state-area-sources section-line" aria-labelledby="state-sources-title">
        <div><span className="kicker">Planning context, not project claims</span><h2 id="state-sources-title">Why these facility types matter in {state.name}.</h2><p>These public sources illustrate real facility conditions in the market. They do not indicate that Partition Stalls supplied or installed the referenced projects.</p></div>
        <div>{page.references.map((reference, index) => <a key={reference.url} href={reference.url} target="_blank" rel="noreferrer"><span>Source 0{index + 1}</span><strong>{reference.label}</strong><p>{reference.context}</p><ArrowUpRight aria-hidden="true" /></a>)}</div>
      </aside>

      <section className="state-area-related section-line" aria-labelledby="state-related-title">
        <div><span className="kicker">Continue planning</span><h2 id="state-related-title">Useful next pages.</h2></div>
        <nav aria-label={`${state.name} related commercial restroom pages`}>
          {state.slug === "new-york" ? <Link href="/service-areas/new-york-city"><span>New York City</span>Commercial bathroom partitions and restroom stalls <ArrowRight aria-hidden="true" /></Link> : null}
          <Link href="/bathroom-partitions"><span>Products</span>Compare five materials <ArrowRight aria-hidden="true" /></Link>
          <Link href="/resources/mounting-styles"><span>Planning</span>Choose a mounting system <ArrowRight aria-hidden="true" /></Link>
          <Link href="/commercial-restroom-accessories"><span>Division 10</span>Coordinate accessories <ArrowRight aria-hidden="true" /></Link>
          <Link href="/service-areas"><span>Coverage</span>View all service areas <ArrowRight aria-hidden="true" /></Link>
          {page.relatedStates.map((slug) => {
            const related = serviceStates.find((item) => item.slug === slug);
            return related ? <Link key={slug} href={`/service-areas/${slug}`}><span>Nearby full service</span>{related.name} <ArrowRight aria-hidden="true" /></Link> : null;
          })}
        </nav>
      </section>

      <QuoteBand quoteHref={quoteHref} title={`Request a commercial bathroom partition quote for ${state.name}.`} copy="Send the project location, plans or measurements, material direction, quantities, accessory scope, schedule, and site conditions. Call or submit the form to start a project-specific review." />
    </>
  );
}
